// Copy or download a table (N5): "Copy" puts it on the clipboard as tab-separated text (paste into Google Sheets or
// Excel and the columns land in place), "CSV" downloads it. Both use shared/csv.ts' formula guard, so a cell like
// "=HYPERLINK(…)" stays text. `rows` is worked out on the click (header row first), so a big table costs nothing until then.
import { Copy, Download } from "lucide-react";
import { toCsv, toTsv } from "../../../shared/csv.ts";
import { planToday } from "../../../shared/day.ts";
import { cx } from "./controls.tsx";
import { useTableText } from "./table-text.ts";
import { toast } from "./toast.tsx";

const slug = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "table";

export function ExportButtons({ name, rows, className }: { name: string; rows: () => unknown[][]; className?: string }) {
  const t = useTableText();
  const copy = async () => {
    const all = rows();
    try {
      await navigator.clipboard.writeText(toTsv(all));
      toast.ok(t.copied(all.length - 1));
    } catch {
      toast.error(t.copyFailed);
    }
  };
  const download = () => {
    const all = rows();
    // A byte-order mark so Excel reads it as UTF-8 (names with accents, ×, —).
    const url = URL.createObjectURL(new Blob([`﻿${toCsv(all)}`], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slug(name)}-${planToday()}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const button = "inline-flex items-center gap-1 rounded px-1 hover:bg-hover hover:text-text";
  return (
    <span className={cx("inline-flex items-center gap-1", className)}>
      <button type="button" className={button} onClick={copy} title={t.copyTitle}>
        <Copy className="size-3" /> {t.copy}
      </button>
      <button type="button" className={button} onClick={download} title={t.csvTitle}>
        <Download className="size-3" /> CSV
      </button>
    </span>
  );
}
